<template>
  <v-container>
    <OverpaymentsNRefundsCriteria :baseCrudKey="baseCrudKey" :userList="userList"
    @loadCRUDList="handleLoadOverpaymentsNRefundsList" @handleSync="handleSync" />
    <CRUDList
      ref="crudList"
      :canAdd="false"
      :canEdit="true"
      :canSave="true"
      :showSelect="true"
      :enableFixedHeaders="true"
      :displayTopPagination="false"
      :headers="headers"
      :selectedItems="selectedItems"
      :baseCrudKey="baseCrudKey"
      :defaultSortBy="defaultSortBy"
      dialogHeaderName="Overpayments & Refunds"
      tableWrapperDynamicClassname='elevation-3 ma-3 pa-1 taxroll-table-wrapper'
      tableDynamicClassname='taxroll-custom-table'
      tableDynamicHeight='350'
      @emitSelectedItem="emitSelectedItem"
      :loadingData="gridLoading.flag"
      :loadingText="gridLoading.text"
      @upsertItem="upsertRecords"
      @loadCRUDList="handleLoadOverpaymentsNRefundsList">
   <template v-slot:[`editedItem`]="{}">
          <OverpaymentsNRefundsDialog
            :baseCrudKey="baseCrudKey"
            :userList="userList"
          />
        </template>
   </CRUDList>
   <OverpaymentFooter @assignUserSelectedRecords="assignUserSelectedRecords"
    @validatePaidRecords="validatePaidRecords" />
  </v-container>
</template>

<script>
import {
  mapMutations, mapActions, mapState, mapGetters,
} from 'vuex';
import CRUDList from '../../../../common/base/crud-2.0/CRUDList.vue';
import { defaultGridLoading, manageGridLoading } from '../../../../../util/shared/vue-global';
import OverpaymentsNRefundsCriteria from './OverpaymentsNRefundsCriteria.vue';
import OverpaymentsNRefundsDialog from './OverpaymentsNRefundsDialog.vue';
import {
  overpaymentsNRefundsHeader, paymentMethodLabel,
} from '../../../TaxInquiry/TaxInquiryUtils/TableHeaders.utils';
import { toIsoDate } from '../../../../../util/shared/tmc-global';
import OverpaymentFooter from './OverpaymentFooter.vue';

export default {
  name: 'OverpaymentsNRefundsList',
  components: {
    CRUDList,
    OverpaymentsNRefundsCriteria,
    OverpaymentsNRefundsDialog,
    OverpaymentFooter,
  },
  data() {
    return {
      baseCrudKey: 'OverpaymentsNRefundsList',
      headers: overpaymentsNRefundsHeader,
      gridLoading: defaultGridLoading(),
      isItFirstCallOrNot: false,
      usernameToFullnameMap: {},
      userList: [],
    };
  },
  async created() {
    // make it deafault
    this.criteriaState.type = 'ovp';
    const users = await this.loadUsers();
    this.loadUsersList(users);
    const map = {};
    (users || []).forEach((u) => {
      if (u.username) map[u.username] = u.fullname || u.username;
    });
    this.usernameToFullnameMap = map;
    this.applyAssignedToFormatter();
    this.applyPaymentMethodFormatter();
  },
  mounted() {
    // TKT-000603: unresolved overpayments can sit open across many tax years, so defaulting
    // this screen to the current tax year silently hid every older-year record. Default to
    // All Years (0) instead - staff can still narrow by year via the criteria dropdown.
    this.isItFirstCallOrNot = true;
    this.criteriaState.taxYear = 0;
    this.handleLoadOverpaymentsNRefundsList();
  },
  computed: {
    ...mapGetters('base/crud', ['criteria', 'item']),
    ...mapState({
      StoreSelItems: (state) => state.tax.taxInquires.selectedOverPaymentsItems,
    }),
    selectedItems: {
      get() {
        return this.StoreSelItems;
      },
      set(value) {
        this.setSelectedOverPaymentsItems(value);
      },
    },
    criteriaState: {
      get() {
        return this.criteria(this.baseCrudKey);
      },
      set(value) {
        this.setCriteria([this.baseCrudKey, value]);
      },
    },
    defaultSortBy() {
      return [
        // {
        //   column: 'assignedTo',
        //   direction: 'asc',
        // },
        {
          column: 'dateOverpaid',
          direction: 'desc',
        },
      ];
    },
  },
  methods: {
    ...mapMutations('base/crud', ['setCriteria']),
    ...mapMutations('tax/taxInquires', [
      'setSelectedOverPaymentsItems',
    ]),
    ...mapActions('tax/taxInquires', [
      'loadOverpaymentsNRefunds',
      'upsertOverpaymentsNRefund',
      'updateAssignToOvPayAndrefund',
      'validatePaidDateOnOverpayment',
      'syncOverpaymentWithWarrants',
    ]),
    ...mapActions('UserAdministration', [
      'loadUsers',
    ]),
    ...mapActions([
      'flashError',
      'flashSuccess',
    ]),
    applyAssignedToFormatter() {
      const map = this.usernameToFullnameMap;
      const idx = this.headers.findIndex((h) => h.value === 'assignedTo');
      if (idx !== -1) {
        this.$set(this.headers, idx, {
          ...this.headers[idx],
          formatter: 'customMethod',
          methodDefinition: (val) => map[val] || val || '',
        });
      }
    },
    applyPaymentMethodFormatter() {
      const idx = this.headers.findIndex((h) => h.value === 'paymentMethod');
      if (idx !== -1) {
        this.$set(this.headers, idx, {
          ...this.headers[idx],
          formatter: 'customMethod',
          methodDefinition: (val) => paymentMethodLabel(val),
        });
      }
    },
    emitSelectedItem(selRow) {
      this.selectedItems = selRow;
    },
    handleLoadOverpaymentsNRefundsList() {
      if (this.isItFirstCallOrNot) {
        const { baseCrudKey } = this;
        const convertToIsoFromDate = this.criteriaState.fromDate
          ? toIsoDate(this.criteriaState.fromDate) : null;
        const convertToIsoToDate = this.criteriaState.toDate
          ? toIsoDate(this.criteriaState.toDate) : null;
        const resolved = this.criteriaState.resolved || false;
        const criteria = {
          ...this.criteriaState,
          ...{
            fromDate: convertToIsoFromDate,
            toDate: convertToIsoToDate,
            resolved,
          },
        };
        manageGridLoading(this, true, 'Searching...');
        this.loadOverpaymentsNRefunds({ baseCrudKey, criteria }).finally(() => {
          manageGridLoading(this, false);
          this.resetSelectedItems();
        });
      }
    },
    async upsertRecords(item) {
      const items = { ...item };
      items.issueDate = item.issueDate ? toIsoDate(item.issueDate) : null;
      items.paidDate = item.paidDate ? toIsoDate(item.paidDate) : null;
      if (item.fund) {
        items.fund = item.fund._id ? item.fund._id : item.fund;
      }
      // send the name shown in the picker so the assignment comment reads the fullname
      // rather than the login. Comment wording only - never stored on the record.
      if (items.assignedTo) {
        items.assignedToFullName = this.usernameToFullnameMap[items.assignedTo] || items.assignedTo;
      }
      // no assignment text is stitched into items.comments here: the backend already logs an
      // assignment comment to Tax Inquiry (and only when the assignee actually changed), whereas
      // anything written here is persisted to the record and replayed on every later save.

      items.warrant = parseInt(item.warrant, 10) || null;
      const { baseCrudKey } = this;
      await this.upsertOverpaymentsNRefund({
        item: items,
        baseCrudKey,
      });
    },
    async validatePaidRecords() {
      const { baseCrudKey } = this;
      if (window.confirm('Are you sure you want to validate paid on overpayment records?') !== true) return;
      await this.validatePaidDateOnOverpayment({ baseCrudKey });
      this.resetSelectedItems();
    },
    async assignUserSelectedRecords(selectedUser, assignedToFullName) {
      const { baseCrudKey } = this;
      const selectedItem = this.item(baseCrudKey);
      if (!selectedUser.length) {
        alert('Please select user for assign to records');
      } else if (!this.selectedItems.length && !selectedItem._id) {
        alert('No record selected. Please check the box next to the record(s) you want to assign.');
      } else if (window.confirm('Are you sure want to assign user selected records') === true) {
        const selectedIds = (this.selectedItems || []).map((item) => item._id);
        const item = {
          ids: selectedIds.length > 1 ? selectedIds : [selectedItem._id],
          assignedTo: selectedUser,
          assignedToFullName,
        };
        await this.updateAssignToOvPayAndrefund({ item, baseCrudKey });
        this.resetSelectedItems();
      }
    },
    loadUsersList(users = []) {
      const taxUsers = (users || []).filter((item) => {
        const { roles } = item;
        return !item.isSystemUser && (roles.includes('TAXADMINISTRATION') || roles.includes('TAXUSER'));
      });
      taxUsers.sort((a, b) => (a.fullname || '').localeCompare(b.fullname || ''));
      this.userList = [{ fullname: '' }, ...taxUsers];
    },
    resetSelectedItems() {
      this.$nextTick(() => {
        this.selectedItems = [];
      });
    },
    async handleSync() {
      const syncWarrantResult = await this.syncOverpaymentWithWarrants();
      if (syncWarrantResult.statusCode === 200) {
        this.handleLoadOverpaymentsNRefundsList();
        this.flashSuccess('Warrant status synched successfully');
      } else {
        this.flashError('There was a problem syncing warrants.');
      }
    },
  },
};
</script>
